// Standalone privacy policy page. Mounted outside the app shell for the same
// reasons as the terms page: public crawlability and no session requirements.

const LAST_UPDATED = "July 8, 2026";
const CONTACT_EMAIL = "privacy@parable.so";
const SECURITY_PAGE_URL = "https://docs.parable.so/security";

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-wiki-bg px-6 py-16 text-wiki-fg-1">
      <div className="anim-fade-in mx-auto max-w-2xl space-y-8">
        <header className="space-y-2">
          <div className="label-caps text-wiki-fg-3">Parable</div>
          <h1 className="font-display text-4xl text-wiki-fg-1 italic">
            Privacy Policy
          </h1>
          <p className="num-mono text-sm text-wiki-fg-3">
            Last updated {LAST_UPDATED}
          </p>
        </header>

        <Section title="What Parable is">
          <p>
            Parable is a company-memory assistant: an ambient capture device and
            desktop app that transcribes your work conversations and keeps the
            results as searchable notes in your private workspace.
          </p>
        </Section>

        <Section title="What we collect">
          <ul className="list-outside list-disc space-y-2 pl-6 marker:text-wiki-fg-3">
            <li>Audio captured from your Parable devices.</li>
            <li>
              Transcripts, summaries, speaker attribution, and metadata
              generated from that audio.
            </li>
            <li>
              If you opt in to speaker recognition: a short voice-test recording
              and the voiceprint derived from it.
            </li>
            <li>
              Workspace-level settings and profile fields needed to run the app.
            </li>
            <li>Tool and integration logs needed for billing and debugging.</li>
          </ul>
        </Section>

        <Section title="How we use data">
          <ul className="list-outside list-disc space-y-2 pl-6 marker:text-wiki-fg-3">
            <li>Power search and retrieval of your captured work content.</li>
            <li>Drive the in-app assistant and background jobs.</li>
            <li>Operate authentication and delivery security.</li>
          </ul>
        </Section>

        <Section title="Where your data is stored">
          <ul className="list-outside list-disc space-y-2 pl-6 marker:text-wiki-fg-3">
            <li>Postgres for structured records and metadata.</li>
            <li>Object storage for captured media and derived artifacts.</li>
            <li>Compute services for speech and retrieval processing.</li>
          </ul>
          <p>
            The full list of processors we use — and what each one receives and
            retains — is published on our{" "}
            <a
              href={SECURITY_PAGE_URL}
              className="text-wiki-accent hover:underline"
            >
              security page
            </a>
            . No third-party large language model provider receives your audio
            or transcripts.
          </p>
        </Section>

        <Section title="Voiceprints and speaker recognition">
          <p>
            Nobody is named in transcripts unless they opt in by recording a
            voice test. The voiceprint we keep is a numeric fingerprint derived
            from that recording — it is not audio and cannot be played back.
            Deleting your voice profile removes the voiceprint and the
            recording, and detaches your name from past transcripts, which
            revert to an anonymous speaker label. Workspace admins can also
            choose to delete voice-test recordings automatically as soon as
            speaker setup completes.
          </p>
        </Section>

        <Section title="Your rights">
          <p>
            Much of this is self-serve: anyone with manage access can delete an
            individual conversation in the app (removing its transcript,
            summary, speaker data, and audio), you can delete your own voice
            profile at any time, and workspace admins control retention in
            Settings → Privacy.
          </p>
          <p>
            To delete all workspace data, contact
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="mx-1 text-wiki-accent hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
            and include your workspace ID. We process deletion requests within
            30 days where possible.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            Conversation audio is kept for at most 30 days by default, and
            workspace admins can shorten that — down to deleting audio the
            moment transcription completes. Transcripts remain in your workspace
            until you delete them, or on the schedule your admin sets (30 days
            to forever). Retention policies apply to existing data and are
            enforced continuously. When data is deleted, it is purged from our
            operational systems immediately; database backups age out within
            about two days. Full retention details are on our{" "}
            <a
              href={SECURITY_PAGE_URL}
              className="text-wiki-accent hover:underline"
            >
              security page
            </a>
            .
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If we materially change this policy, we update this page and notify
            active users by email. The "Last updated" date always reflects the
            current version.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions, requests, or concerns:{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-wiki-accent hover:underline"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl text-wiki-fg-1 italic">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-wiki-fg-2">
        {children}
      </div>
    </section>
  );
}
